import React, {FunctionComponent, useState, useRef, useEffect} from 'react';
import {useAnimationFrame} from '@focusritegroup/web-ui';
import {Spacer} from '../../styles/page';
import {ButtonVariant} from '../Button';
import {
  Artwork,
  Image,
  Name,
  Title,
  Wrapper,
  Controls,
  Duration,
  PlayButton,
  StopButton,
  NextButton,
  PrevButton,
  ProgressOuter,
  ProgressInner,
  Background,
  BackgroundImage,
  AudioPlayerCanvas,
} from './AudioPlayer.styles';
import {AudioPlayerProps, AudioPlayerLayoutProps} from './AudioPlayer.types';
import Colours from '../../constants/colours';
import {
  getCommonColours,
  getImageData,
  getForegroundColour,
} from './AudioPlayer.utils';

const formatTime = (time: number): string => {
  return (
    Math.floor(time / 60) +
    ':' +
    Math.floor(time % 60)
      .toString()
      .padStart(2, '0')
  );
};

const AudioPlayerLayout: FunctionComponent<AudioPlayerLayoutProps> = ({
  pack,
  title,
  onPlay,
  onNext,
  onPrev,
  progress,
  currentTime,
  isPlaying,
  imageRef,
  foregroundColour,
  onImageLoaded,
}) => {
  return (
    <Wrapper>
      {title && <Title foregroundColour={foregroundColour}>{title}</Title>}
      <Spacer />
      <Spacer />
      <Artwork>
        <Image
          ref={imageRef}
          src={pack.imageUrl}
          onLoad={onImageLoaded}
          crossOrigin="anonymous"
        />
        <ProgressOuter>
          <ProgressInner
            style={{transform: `translateX(${-100 + progress * 100}%)`}}
          />
        </ProgressOuter>
      </Artwork>
      <Spacer />
      <Spacer />
      <Name style={{color: foregroundColour}}>{pack.name}</Name>
      <Duration style={{color: foregroundColour}}>
        {formatTime(currentTime)}
      </Duration>
      <Spacer />
      <Spacer />
      <Controls data-test-id="controls">
        <PrevButton
          data-test-id="prev"
          onClick={onPrev}
          variant={ButtonVariant.WHITE_OUTLINE}
          foregroundColour={foregroundColour}
        />
        {isPlaying ? (
          <StopButton
            data-test-id="stop"
            onClick={onPlay}
            variant={ButtonVariant.WHITE}
            foregroundColour={foregroundColour}
          />
        ) : (
          <PlayButton
            data-test-id="play"
            onClick={onPlay}
            variant={ButtonVariant.WHITE}
            foregroundColour={foregroundColour}
          />
        )}
        <NextButton
          data-test-id="next"
          onClick={onNext}
          variant={ButtonVariant.WHITE_OUTLINE}
          foregroundColour={foregroundColour}
        />
      </Controls>
    </Wrapper>
  );
};

export const AudioPlayer: FunctionComponent<AudioPlayerProps> = ({
  packs,
  title,
  shadow = false,
  autoNext = true,
  autoPlay = false,
  onPackPreviewed,
}) => {
  const packPreviewedTimer = useRef<number>();
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [progress, setProgress] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [current, setCurrent] = useState<number>(0);
  const [userInteracted, setUserInteracted] = useState<boolean>(false);
  const [canProcessArtwork, setCanProcessArtwork] = useState<boolean>(true);
  const [foregroundColour, setForegroundColour] = useState<string>(
    Colours.white
  );

  React.useEffect(() => {
    if (autoPlay || userInteracted) {
      start();
    }

    return () => clearTimeout(packPreviewedTimer.current);
  }, [current]);

  const handleTogglePlayback = () => {
    if (isPlaying === false) {
      start();
    } else {
      stop();
    }
  };

  const start = () => {
    audioRef.current?.load();
    audioRef.current?.play().catch((error) => {
      // this can throw when cycling through the sources
      // quickly but we're handling this ourselves below
      // so these errors are currently silenced
      //
      // tried working with the readyState but this gives
      // audio/visual mismatch bugs, better solution is
      // to preload which is coming later (also improves
      // image loading/transitions)
    });

    setIsPlaying(true);

    if (onPackPreviewed) {
      // only consider "previewed" after an elapsed amount of playback time
      const previewedTimeMs = 1000;

      packPreviewedTimer.current = setTimeout(
        () => onPackPreviewed(packs[current]),
        previewedTimeMs
        // typescript is getting confused between NodeJs Timeout and browser timeout
      ) as unknown as number;
    }
  };

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current!.currentTime = 0;
    setIsPlaying(false);

    clearTimeout(packPreviewedTimer.current);
  };

  const next = () => {
    let next = current + 1;
    next = next > packs.length - 1 ? 0 : next;
    audioRef.current!.currentTime = 0;

    clearTimeout(packPreviewedTimer.current);

    setCurrent(next);
    setUserInteracted(true);
  };

  const prev = () => {
    let prev;

    if (audioRef.current!.currentTime < 1) {
      prev = current - 1;
      prev = prev < 0 ? packs.length - 1 : prev;
    } else {
      prev = current;
    }

    audioRef.current!.currentTime = 0;

    clearTimeout(packPreviewedTimer.current);

    setCurrent(prev);
    setUserInteracted(true);
  };

  const handleEnded = () => {
    stop();

    if (current < packs.length - 1 && autoNext) {
      next();
    }
  };

  const processArtwork = async () => {
    if (imageRef.current) {
      try {
        const image = getImageData(imageRef, canvasRef);
        const commonColours = getCommonColours(image.data, 10);
        const foregroundColour = getForegroundColour(commonColours);
        setForegroundColour(foregroundColour);
      } catch (error) {
        // unable to read from the image so gracefully degrade
        // by no longer showing the background image
        //
        // Oct20: Currently a known issue in safari throwing a security
        // error (cors) when processing image data
        setCanProcessArtwork(false);
      }
    }
  };

  useAnimationFrame(() => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime / audioRef.current.duration);
    }
  });

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('ended', handleEnded);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleEnded);
      }
    };
  });

  return (
    <Background shadow={shadow}>
      {packs.length > 0 && (
        <>
          <AudioPlayerLayout
            title={title}
            imageRef={imageRef}
            pack={packs[current]}
            progress={progress}
            onPlay={handleTogglePlayback}
            onNext={next}
            onPrev={prev}
            isPlaying={isPlaying}
            currentTime={audioRef.current ? audioRef.current.currentTime : 0}
            foregroundColour={foregroundColour}
            onImageLoaded={processArtwork}
          />
          {canProcessArtwork && (
            <BackgroundImage
              crossOrigin="anonymous"
              src={packs[current].imageUrl}
            />
          )}
          <audio ref={audioRef}>
            <source src={packs[current].previewUrl} />
          </audio>
          <AudioPlayerCanvas ref={canvasRef}></AudioPlayerCanvas>
        </>
      )}
    </Background>
  );
};
